import { atom, useAtom, useAtomValue } from 'jotai';
import { atomFamily } from 'jotai-family';
import { atomWithDefault } from 'jotai/utils';
import { useCallback } from 'react';

import { selectedViewIdPFJ } from '../state/ViewStateJ';
import { useSaveMprStateInView } from './mprViewActions';
import { mpReportingGridApi } from './MultiPeriodGrid';
import { createMprViewSettings, IMprViewSettings } from './MultiPeriodReporting.schemas';
import { useMprColDefs } from './multiperiodReportingGridData';
import { getMprViewColumns, uniqueMprItems, uniqueMprPeriods } from './multiPeriodReportingUtils';

export const savedMprViewConfigAtom = atomFamily(() => atom<IMprViewSettings | null>(null));

const draftMprSettingsAtom = atomFamily((viewId: number) =>
  atomWithDefault<IMprViewSettings>((get) => get(savedMprViewConfigAtom(viewId)) ?? createMprViewSettings())
);
const draftMprColumnsAtom = atomFamily((viewId: number) =>
  atom((get) => getMprViewColumns(get(draftMprSettingsAtom(viewId))))
);

export function useMprColumnConfigs() {
  const updateView = useSaveMprStateInView();
  const viewId = useAtomValue(selectedViewIdPFJ);
  const [draft, setDraft] = useAtom(draftMprSettingsAtom(viewId));
  const draftColumns = useAtomValue(draftMprColumnsAtom(viewId));
  const [saved, saveSettings] = useAtom(savedMprViewConfigAtom(viewId));
  const gridApi = useAtomValue(mpReportingGridApi);
  const settingsToColDefs = useMprColDefs();

  const saveConfigState = useCallback(
    async (updateGridColDefs = true) => {
      const settings: IMprViewSettings = {
        ...draft,
        items: uniqueMprItems(draft.items),
        periods: uniqueMprPeriods(draft.periods),
      };
      saveSettings((prev) => ({ ...(prev ?? createMprViewSettings()), ...settings }));

      if (updateGridColDefs) {
        gridApi?.setGridOption('columnDefs', settingsToColDefs(settings));
      }
      updateView({ multiPeriodReportingConfig: settings });
    },
    [draft, saveSettings, gridApi, settingsToColDefs, updateView]
  );

  const resetDrafts = useCallback(() => {
    setDraft(saved ?? createMprViewSettings());
  }, [saved, setDraft]);

  return {
    draft,
    setDraft,
    draftColumnConfigs: draftColumns,
    saveConfigState,
    resetDrafts,
  };
}
